import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  getParametricAiErrorMessage,
  requestParametricAiSuggestion,
} from "./services/parametricAiApi";
import { buildParametricAiDesignContext } from "./services/parametricAiContext";
import useParametricAiSessionStore from "./store/parametricAiSessionStore";


const MAX_PROMPT_LENGTH = 500;


function displayValue(value) {
  if (value && typeof value === "object") {
    const layer = value.layer || "";
    const datatype = value.datatype || "";
    return [layer, datatype].filter(Boolean).join(" / ") || "—";
  }

  if (value === undefined || value === null || value === "") return "—";
  return String(value);
}


function confirmationIntent(message) {
  if (/\d/.test(message)) return "instruction";

  const normalized = message
    .trim()
    .toLowerCase()
    .replace(/[^a-z\s']/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  const negative = [
    /^(no|nope|cancel|stop)$/,
    /^(no|nope),? (cancel|stop|do not apply)( it| these changes)?$/,
    /^(do not|don't) (apply|change)( it| anything| these changes)?$/,
  ];
  if (negative.some((pattern) => pattern.test(normalized))) return "reject";

  const wordCount = normalized.split(" ").filter(Boolean).length;
  const startsAffirmative = /^(yes|yeah|yep|ok|okay|sure|confirm)\b/.test(
    normalized
  );
  const startsWithAction = /^(apply|confirm|go ahead|do it|proceed)\b/.test(
    normalized
  );
  const containsNewEdit = /\b(set|change|update|increase|decrease)\b/.test(
    normalized
  );

  if (
    wordCount <= 10 &&
    !containsNewEdit &&
    (startsAffirmative || startsWithAction)
  ) {
    return "accept";
  }

  return "instruction";
}


const ParametricAiPrompt = ({
  patternType,
  cellName,
  currentParameters,
  onConfirmApply,
  onConfirmActions,
  availableLayers = [],
  isActive = false,
  disabled = false,
  fillContainer = false,
  showHeader = true,
  largeComposer = false,
}) => {
  const {
    messages,
    draft,
    pendingProposal,
    initializeConversation,
    appendMessage: appendSessionMessage,
    setDraft,
    setPendingProposal,
  } = useParametricAiSessionStore();
  const [isSending, setIsSending] = useState(false);
  const requestVersionRef = useRef(0);
  const messagesEndRef = useRef(null);
  const cardRef = useRef(null);
  const [availableHeight, setAvailableHeight] = useState(null);

  const parameterSignature = useMemo(
    () => JSON.stringify(currentParameters || []),
    [currentParameters]
  );
  const targetSignature = `${cellName || ""}\u0000${patternType || ""}`;
  const parameterContextRef = useRef({
    targetSignature,
    parameterSignature,
  });

  const appendMessage = (role, text, extra = {}) => {
    appendSessionMessage(cellName, role, text, extra);
  };

  useEffect(() => {
    initializeConversation(cellName);
  }, [cellName, initializeConversation]);

  useEffect(() => {
    const previous = parameterContextRef.current;
    if (
      previous.targetSignature === targetSignature &&
      previous.parameterSignature !== parameterSignature
    ) {
      requestVersionRef.current += 1;
      setPendingProposal(null);
    }
    parameterContextRef.current = { targetSignature, parameterSignature };
  }, [parameterSignature, setPendingProposal, targetSignature]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView?.({ block: "nearest" });
  }, [messages, isSending]);

  useEffect(() => {
    if (!isActive || fillContainer) return undefined;

    let animationFrame = null;
    const updateAvailableHeight = () => {
      if (animationFrame !== null) cancelAnimationFrame(animationFrame);

      animationFrame = requestAnimationFrame(() => {
        const cardTop = cardRef.current?.getBoundingClientRect().top;
        if (!Number.isFinite(cardTop)) return;

        const nextHeight = Math.max(
          180,
          Math.floor(window.innerHeight - cardTop - 36)
        );
        setAvailableHeight((current) =>
          current === nextHeight ? current : nextHeight
        );
      });
    };

    updateAvailableHeight();
    window.addEventListener("resize", updateAvailableHeight);

    const resizeObserver =
      typeof ResizeObserver === "function"
        ? new ResizeObserver(updateAvailableHeight)
        : null;
    if (cardRef.current?.parentElement) {
      resizeObserver?.observe(cardRef.current.parentElement);
    }

    return () => {
      window.removeEventListener("resize", updateAvailableHeight);
      resizeObserver?.disconnect();
      if (animationFrame !== null) cancelAnimationFrame(animationFrame);
    };
  }, [fillContainer, isActive]);

  const canSend =
    !disabled &&
    !isSending &&
    Boolean(cellName) &&
    Boolean(patternType) &&
    draft.trim().length > 0 &&
    Array.isArray(currentParameters) &&
    currentParameters.length > 0;

  const requestProposal = async (instruction, conversation) => {
    const requestVersion = requestVersionRef.current + 1;
    requestVersionRef.current = requestVersion;
    setIsSending(true);

    try {
      const proposal = await requestParametricAiSuggestion({
        patternType,
        cellName,
        prompt: instruction,
        currentParameters,
        designContext: buildParametricAiDesignContext({
          cellName,
          patternType,
          availableLayers,
        }),
        conversation,
      });

      if (requestVersionRef.current !== requestVersion) return;

      if (
        proposal?.confirmation?.required &&
        Array.isArray(proposal?.changes) &&
        proposal.changes.length > 0
      ) {
        setPendingProposal({
          ...proposal,
          _sessionContext: {
            cellName,
            patternType,
            parameterSignature,
          },
        });
        appendMessage("assistant", proposal.message, {
          changes: proposal.changes,
        });
      } else {
        setPendingProposal(null);
        appendMessage(
          "assistant",
          proposal?.message || "I could not find a parameter change in that message.",
          { tone: "warning" }
        );
      }
    } catch (error) {
      if (requestVersionRef.current !== requestVersion) return;
      appendMessage("assistant", getParametricAiErrorMessage(error), {
        tone: "warning",
      });
    } finally {
      if (requestVersionRef.current === requestVersion) {
        setIsSending(false);
      }
    }
  };

  const applyPendingProposal = async (proposal) => {
    const proposalCellName = proposal?.target?.cellName || cellName;
    const proposalPatternType = proposal?.target?.patternType || patternType;

    if (proposalCellName !== cellName || proposalPatternType !== patternType) {
      setPendingProposal(null);
      appendMessage(
        "assistant",
        "The selected PCell changed. Send the instruction again for the current selection.",
        { tone: "warning" }
      );
      return;
    }

    if (
      proposal?._sessionContext?.parameterSignature &&
      proposal._sessionContext.parameterSignature !== parameterSignature
    ) {
      setPendingProposal(null);
      appendMessage(
        "assistant",
        "The target parameters changed after this proposal was created. Send the instruction again for the current values.",
        { tone: "warning" }
      );
      return;
    }

    setIsSending(true);
    try {
      if (
        Array.isArray(proposal?.actions) &&
        proposal.actions.length > 0
      ) {
        if (typeof onConfirmActions !== "function") {
          throw new Error("The AI action executor is not available.");
        }
        await onConfirmActions({
          cellName: proposalCellName,
          patternType: proposalPatternType,
          actions: proposal.actions,
        });
      } else {
        if (
          !Array.isArray(proposal?.proposedParameters) ||
          typeof onConfirmApply !== "function"
        ) {
          throw new Error("I could not apply that proposal safely.");
        }
        await onConfirmApply({
          cellName: proposalCellName,
          patternType: proposalPatternType,
          proposedParameters: proposal.proposedParameters,
        });
      }
      setPendingProposal(null);
      appendMessage(
        "assistant",
        Array.isArray(proposal?.actions) && proposal.actions.length > 0
          ? "Done — the confirmed parametric actions were applied."
          : `Done — changes were applied to ${proposalCellName}.`,
        { tone: "success" }
      );
    } catch (error) {
      appendMessage("assistant", getParametricAiErrorMessage(error), {
        tone: "warning",
      });
    } finally {
      setIsSending(false);
    }
  };

  const sendMessage = async () => {
    if (!canSend) return;

    const instruction = draft.trim();
    const proposalAtSend = pendingProposal;
    setDraft("");
    appendMessage("user", instruction);

    if (proposalAtSend) {
      const intent = confirmationIntent(instruction);
      if (intent === "accept") {
        await applyPendingProposal(proposalAtSend);
        return;
      }
      if (intent === "reject") {
        setPendingProposal(null);
        appendMessage("assistant", "Okay — I did not change the canvas.");
        return;
      }

      setPendingProposal(null);
    }

    await requestProposal(
      instruction,
      [...messages, { role: "user", text: instruction }]
        .slice(-8)
        .map(({ role, text }) => ({ role, text }))
    );
  };

  return (
    <section
      ref={cardRef}
      className="parametric-ai-chat-card"
      style={{
        ...cardStyle,
        ...(fillContainer ? floatingCardStyle : {}),
        height: fillContainer
          ? "100%"
          : availableHeight === null
            ? "calc(100dvh - 380px)"
            : `${availableHeight}px`,
      }}
      aria-label="PCell agent chat"
      onPointerDown={(event) => event.stopPropagation()}
      onClick={(event) => event.stopPropagation()}
    >
      {showHeader && (
        <div style={headerStyle}>
          <div style={agentTitleStyle}>
            <span style={onlineDotStyle} />
            PCell Agent
          </div>
          <span style={targetBadgeStyle} title={cellName}>
            {cellName}
          </span>
        </div>
      )}

      <div
        role="log"
        aria-live="polite"
        className="parametric-ai-dark-scrollbar"
        style={messageListStyle}
      >
        {messages.map((message) => (
          <div key={message.id} style={messageRowStyle(message.role)}>
            {message.role === "assistant" && (
              <span style={agentAvatarStyle}>AI</span>
            )}
            <div style={messageBubbleStyle(message.role, message.tone)}>
              <div>{message.text}</div>
              {Array.isArray(message.changes) && message.changes.length > 0 && (
                <div style={changeListStyle}>
                  {message.changes.map((change, changeIndex) => (
                    <div
                      key={`${change.name}-${change.operation || "change"}-${changeIndex}`}
                      style={changeRowStyle}
                    >
                      <span style={parameterNameStyle}>{change.name}</span>
                      <span style={oldValueStyle}>{displayValue(change.oldValue)}</span>
                      <span style={arrowStyle}>→</span>
                      <span style={newValueStyle}>{displayValue(change.newValue)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}

        {isSending && (
          <div style={messageRowStyle("assistant")}>
            <span style={agentAvatarStyle}>AI</span>
            <div style={typingBubbleStyle} aria-label="PCell Agent is working">
              <span style={typingDotStyle} />
              <span style={typingDotStyle} />
              <span style={typingDotStyle} />
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      <div style={{ ...composerStyle, ...(largeComposer ? largeComposerStyle : {}) }}>
        <textarea
          rows={largeComposer ? 3 : 1}
          value={draft}
          maxLength={MAX_PROMPT_LENGTH}
          disabled={disabled || isSending}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            event.stopPropagation();
            if (event.key === "Enter" && !event.shiftKey) {
              event.preventDefault();
              sendMessage();
            }
          }}
          placeholder={
            pendingProposal ? "Reply yes or no…" : "Message PCell Agent…"
          }
          aria-label="Message PCell Agent"
          className="parametric-ai-dark-scrollbar"
          style={{
            ...promptStyle,
            ...(largeComposer ? largePromptStyle : {}),
          }}
        />
        <button
          type="button"
          disabled={!canSend}
          onClick={sendMessage}
          aria-label="Send message"
          title="Send"
          style={sendButtonStyle(canSend)}
        >
          ➤
        </button>
      </div>

      <style>{`
        .parametric-ai-dark-scrollbar {
          scrollbar-width: thin;
          scrollbar-color: #315c66 transparent;
        }

        .parametric-ai-dark-scrollbar::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }

        .parametric-ai-dark-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }

        .parametric-ai-dark-scrollbar::-webkit-scrollbar-thumb {
          min-height: 28px;
          border: 2px solid transparent;
          border-radius: 999px;
          background: #315c66;
          background-clip: padding-box;
        }

        .parametric-ai-dark-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #3f7783;
          background-clip: padding-box;
        }

        .parametric-ai-dark-scrollbar::-webkit-scrollbar-corner {
          background: transparent;
        }
      `}</style>
    </section>
  );
};


const cardStyle = {
  display: "flex",
  flexDirection: "column",
  minHeight: "180px",
  marginBottom: 0,
  padding: "9px",
  boxSizing: "border-box",
  overflow: "hidden",
  border: "1px solid #315866",
  borderRadius: "8px",
  background: "linear-gradient(145deg, #172126, #1d2529)",
  boxShadow: "0 5px 14px rgba(0, 0, 0, 0.22)",
};

const floatingCardStyle = {
  minHeight: 0,
  margin: 0,
  padding: "11px",
  border: "none",
  borderRadius: 0,
  background: "linear-gradient(145deg, #11191d, #162126)",
  boxShadow: "none",
};

const headerStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "8px",
  marginBottom: "8px",
};

const agentTitleStyle = {
  display: "flex",
  alignItems: "center",
  gap: "6px",
  color: "#65e4ef",
  fontSize: "12px",
  fontWeight: 700,
};

const onlineDotStyle = {
  width: "7px",
  height: "7px",
  borderRadius: "50%",
  background: "#65dfa0",
  boxShadow: "0 0 7px rgba(101, 223, 160, 0.7)",
};

const targetBadgeStyle = {
  maxWidth: "145px",
  padding: "3px 6px",
  overflow: "hidden",
  border: "1px solid #315866",
  borderRadius: "10px",
  color: "#b8f7ff",
  background: "#10242b",
  fontSize: "10px",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const messageListStyle = {
  display: "flex",
  flexDirection: "column",
  flex: 1,
  gap: "7px",
  minHeight: 0,
  padding: "2px 2px 7px",
  overflowY: "auto",
};

const messageRowStyle = (role) => ({
  display: "flex",
  alignItems: "flex-start",
  justifyContent: role === "user" ? "flex-end" : "flex-start",
  gap: "5px",
});

const agentAvatarStyle = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: "20px",
  height: "20px",
  flexShrink: 0,
  border: "1px solid #33717d",
  borderRadius: "50%",
  color: "#7deaf3",
  background: "#10292f",
  fontSize: "8px",
  fontWeight: 800,
};

const messageBubbleStyle = (role, tone) => {
  let border = role === "user" ? "#31798a" : "#31535c";
  let background = role === "user" ? "#174552" : "#18282d";
  let color = "#e5f1f2";

  if (tone === "warning") {
    border = "#765843";
    background = "#30241c";
    color = "#ffd2a6";
  } else if (tone === "success") {
    border = "#2e7857";
    background = "#142a20";
    color = "#a3f7c8";
  }

  return {
    maxWidth: "86%",
    padding: "7px 8px",
    border: `1px solid ${border}`,
    borderRadius: role === "user" ? "8px 3px 8px 8px" : "3px 8px 8px 8px",
    color,
    background,
    fontSize: "10px",
    lineHeight: 1.4,
    overflowWrap: "anywhere",
  };
};

const changeListStyle = {
  display: "flex",
  flexDirection: "column",
  gap: "3px",
  marginTop: "6px",
  paddingTop: "5px",
  borderTop: "1px solid rgba(143, 207, 216, 0.2)",
};

const changeRowStyle = {
  display: "grid",
  gridTemplateColumns: "minmax(62px, 1fr) minmax(34px, auto) 12px minmax(34px, auto)",
  alignItems: "center",
  gap: "4px",
};

const parameterNameStyle = {
  overflow: "hidden",
  color: "#ffcf5a",
  fontWeight: 700,
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const oldValueStyle = {
  color: "#a9b4b7",
  textDecoration: "line-through",
};

const arrowStyle = {
  color: "#789097",
  textAlign: "center",
};

const newValueStyle = {
  color: "#71f0a9",
  fontWeight: 700,
};

const typingBubbleStyle = {
  display: "flex",
  gap: "3px",
  padding: "8px 10px",
  border: "1px solid #31535c",
  borderRadius: "3px 8px 8px 8px",
  background: "#18282d",
};

const typingDotStyle = {
  width: "4px",
  height: "4px",
  borderRadius: "50%",
  background: "#77cbd4",
};

const composerStyle = {
  display: "flex",
  alignItems: "flex-end",
  gap: "6px",
  padding: "5px",
  border: "1px solid #3b5158",
  borderRadius: "7px",
  background: "#101719",
};

const largeComposerStyle = {
  alignItems: "flex-end",
  padding: "8px",
  borderColor: "#3a626b",
  background: "linear-gradient(145deg, #0e1619, #111d21)",
  boxShadow: "inset 0 0 0 1px rgba(85, 216, 230, 0.03)",
};

const promptStyle = {
  width: "100%",
  minHeight: "30px",
  maxHeight: "66px",
  padding: "7px 5px",
  resize: "none",
  boxSizing: "border-box",
  border: "none",
  outline: "none",
  color: "#edf7f8",
  background: "transparent",
  fontFamily: "inherit",
  fontSize: "11px",
  lineHeight: 1.35,
};

const largePromptStyle = {
  minHeight: "72px",
  maxHeight: "124px",
  padding: "8px 6px",
  overflowY: "auto",
  fontSize: "12px",
  lineHeight: 1.45,
};

const sendButtonStyle = (enabled) => ({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: "30px",
  height: "30px",
  flexShrink: 0,
  padding: 0,
  border: `1px solid ${enabled ? "#37b9c8" : "#344246"}`,
  borderRadius: "6px",
  color: enabled ? "#061315" : "#657378",
  background: enabled ? "#55d8e6" : "#222b2e",
  fontSize: "13px",
  cursor: enabled ? "pointer" : "not-allowed",
});


export default ParametricAiPrompt;
